// src/pages/Admin/ConciergeSearchBar.jsx
import React, { useCallback, useEffect, useState } from "react";
import axios from "../../../../../utils/cmsAxios";
import { Calendar } from "lucide-react";

import PeriodPicker, { toKstIsoRange } from "../../../CommonComponents/PeriodPicker";
import ConciergeExcelUploadModal from "./ConciergeExcelUploadModal";

const STATUS_OPTIONS = [
  { value: "", label: "전체" },
  { value: "PENDING", label: "대기" },
  { value: "APPROVED", label: "승인" },
  { value: "PAUSED", label: "일시정지" },
  { value: "STOPPED", label: "종료" },
];

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const EMPTY_FILTERS = {};

const ConciergeSearchBar = ({ title, onSearch, fetchList, initialFilters = EMPTY_FILTERS, itemsCount = 0 }) => {
  const [searchField, setSearchField] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [dateField, setDateField] = useState("created_at");

  const [showApplyPicker, setShowApplyPicker] = useState(false);
  const [applyMode, setApplyMode] = useState("range");
  const [applySingle, setApplySingle] = useState(null);
  const [applyRange, setApplyRange] = useState({ from: null, to: null });

  const [showDbUpload, setShowDbUpload] = useState(false);

  useEffect(() => {
    setSearchField(initialFilters.search_field || "all");
    setKeyword(initialFilters.keyword || "");
    setStatus(initialFilters.status || "");
    setDateField(initialFilters.date_field || "created_at");

    const start = parseDate(initialFilters.apply_start);
    const end = parseDate(initialFilters.apply_end);
    if (start || end) {
      setApplyMode("range");
      setApplySingle(null);
      setApplyRange({ from: start, to: end || start });
    } else {
      setApplyMode("range");
      setApplySingle(null);
      setApplyRange({ from: null, to: null });
    }
  }, [initialFilters]);

  const handleFilesSelected = () => {
    // DB 등록 모달 내부에서 파일 검증/매핑을 처리한다.
  };

  const getCurrentApplyParams = useCallback(() => {
    if (applyMode === "single" && applySingle) {
      return toKstIsoRange(applySingle, applySingle);
    }
    if (applyMode === "range" && applyRange.from) {
      return toKstIsoRange(applyRange.from, applyRange.to || applyRange.from);
    }
    return {};
  }, [applyMode, applySingle, applyRange]);

  const fmtDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  const handleSearchClick = () => {
    const cur = getCurrentApplyParams();
    onSearch?.({
      search_field: searchField,
      keyword: keyword || undefined,
      status: status || undefined,
      date_field: dateField,
      apply_start: cur.join_start || undefined,
      apply_end: cur.join_end || undefined,
    });
  };

  const handleResetClick = () => {
    setSearchField("all");
    setKeyword("");
    setStatus("");
    setDateField("created_at");
    setApplyMode("range");
    setApplySingle(null);
    setApplyRange({ from: null, to: null });
    onSearch?.({});
  };

  const handleSubmitConcierge = async (rows) => {
    await axios.post(
      `${process.env.REACT_APP_FASTAPI_ADS_URL}/concierge/submit/excel`,
      { rows }
    );
    fetchList?.();
  };

  return (
    <section className="px-4 py-4 w-full overflow-visible border-b border-neutral-200">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>

      <div className="rounded-lg bg-white px-3 py-2">
        <div className="grid grid-cols-1 md:grid-cols-[160px_minmax(0,1fr)] gap-3 mb-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-neutral-500">검색 항목</label>
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="h-9 w-full rounded-md border border-neutral-300 bg-white px-2 text-sm"
            >
              <option value="all">전체</option>
              <option value="name">이름</option>
              <option value="store_name">매장명</option>
            </select>
          </div>

          <div className="flex flex-col gap-1 w-full">
            <label className="text-sm text-neutral-500">검색어</label>
            <input
              value={keyword}
              maxLength={100}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder={
                searchField === "name"
                  ? "이름 입력"
                  : searchField === "store_name"
                    ? "매장명 입력"
                    : "이름 또는 매장명 입력"
              }
              className="h-9 rounded-md border border-neutral-300 bg-white px-3 text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-2 w-full">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-neutral-500">상태</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-9 rounded-md border border-neutral-300 bg-white px-2 text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-neutral-500">날짜 기준</label>
            <select
              value={dateField}
              onChange={(e) => setDateField(e.target.value)}
              className="h-9 rounded-md border border-neutral-300 bg-white px-2 text-sm"
            >
              <option value="created_at">신청일</option>
              <option value="approved_at">승인일</option>
            </select>
          </div>

          <div className="flex flex-col gap-1 md:col-span-2 relative">
            <label className="text-sm text-neutral-500">
              {dateField === "approved_at" ? "승인일" : "신청일"}
            </label>
            <button
              type="button"
              onClick={() => setShowApplyPicker(true)}
              className="h-9 w-full inline-flex items-center justify-between gap-2 rounded-md border border-neutral-300 bg-white px-2 text-sm whitespace-nowrap"
            >
              <span className="truncate">
                {(() => {
                  const cur = getCurrentApplyParams();
                  if (cur.join_start && cur.join_end) {
                    const s = new Date(cur.join_start);
                    const e = new Date(cur.join_end);
                    return `${fmtDate(s)} ~ ${fmtDate(e)}`;
                  }
                  return "-";
                })()}
              </span>
              <Calendar />
            </button>

            {showApplyPicker && (
              <>
                <div
                  className="fixed inset-0 bg-black/30 z-[40]"
                  onClick={() => setShowApplyPicker(false)}
                />
                <div className="absolute z-[50] mt-2 rounded-lg border bg-white shadow-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={applyMode}
                      onChange={(e) => setApplyMode(e.target.value)}
                      className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                    >
                      <option value="single">하루</option>
                      <option value="range">기간</option>
                    </select>
                  </div>

                  <PeriodPicker
                    mode={applyMode}
                    value={applyMode === "single" ? applySingle : applyRange}
                    onChange={(val) => {
                      if (applyMode === "single") {
                        setApplySingle(val || null);
                      } else {
                        setApplyRange(val ?? { from: null, to: null });
                      }
                    }}
                    onConfirm={() => setShowApplyPicker(false)}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between py-2">
          <div />

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleResetClick}
              className="h-9 px-6 rounded-md border border-neutral-300 text-white bg-neutral-500 hover:bg-neutral-400"
            >
              초기화
            </button>
            <button
              type="button"
              onClick={handleSearchClick}
              className="h-9 px-9 rounded-md text-white shadow-sm bg-[#7864F9] hover:bg-violet-600"
            >
              검색
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowDbUpload(true)}
            className="h-9 px-6 rounded-md border border-neutral-300 bg-neutral-200 text-neutral-700 hover:bg-neutral-300 text-sm"
          >
            DB 등록
          </button>
        </div>

        <ConciergeExcelUploadModal
          open={showDbUpload}
          onClose={() => setShowDbUpload(false)}
          itemsCount={itemsCount}
          onFilesSelected={handleFilesSelected}
          onSubmit={handleSubmitConcierge}
        />
      </div>
    </section>
  );
};

export default ConciergeSearchBar;
